import moment from 'moment';
import type { DashboardSmallUser } from 'api/models/DashboardSmallUser';
import { WorkboardTask } from 'api/models/WorkboardTask';
import { CALENDAR_DATE_FORMAT } from 'dashboard409a/common/constants/calendar';
import type { SelectedUserWithGroup } from 'dashboard409a/pages/Workboard/UserGroups/UserGroups.types';
import type { RecurrenceEndMode } from './CreateWorkboardTaskModal.constants';

export type CreateWorkboardTaskFormState = {
  name: string;
  startsOn: Date | null;
  occurrenceDate: Date | null;
  status: WorkboardTask.status;
  assignedTo: SelectedUserWithGroup[];
  assignedTaskManager: SelectedUserWithGroup[];
  linkToRelatedDocuments: string;
  fileReferenceFolder: string;
  isWaiting: boolean;
  recurrenceFrequency: WorkboardTask.recurrence_frequency;
  recurrenceInterval: number;
  recurrenceByWeekday: string[];
  recurrenceBySetpos: number | null;
  recurrenceEndMode: RecurrenceEndMode;
  recurrenceEndsOn: Date | null;
  recurrenceCount: number | null;
};

const WEEKDAY_CODES = ['SU', 'MO', 'TU', 'WE', 'TH', 'FR', 'SA'] as const;

export const getCustomUserDisplayName = (user: DashboardSmallUser | null): string => {
  if (!user) {
    return 'Unassigned';
  }

  const fullName = [user.first_name, user.last_name].filter(Boolean).join(' ');
  return fullName || user.email || 'Unknown User';
};

export const getCustomUsersDisplayName = (users: SelectedUserWithGroup[]): string => {
  if (users.length === 0) {
    return 'Unassigned';
  }

  if (users.length === 1) {
    return getCustomUserDisplayName(users[0]);
  }

  return `${getCustomUserDisplayName(users[0])}, +${users.length - 1} more`;
};

export const getCustomUserAvatarData = (user: DashboardSmallUser | null) =>
  user?.picture_url ? [{ picture_url: user.picture_url }] : [];

export const getCustomUsersAvatarData = (users: SelectedUserWithGroup[]) =>
  users.flatMap(user => getCustomUserAvatarData(user));

export const getWeekdayCodeFromDate = (date: Date) => WEEKDAY_CODES[moment(date).day()];

export const getDefaultFormState = (defaultStartsOn?: Date | null): CreateWorkboardTaskFormState => ({
  name: '',
  startsOn: defaultStartsOn ?? new Date(),
  occurrenceDate: defaultStartsOn ?? new Date(),
  status: WorkboardTask.status.PENDING,
  assignedTo: [],
  assignedTaskManager: [],
  linkToRelatedDocuments: '',
  fileReferenceFolder: '',
  isWaiting: false,
  recurrenceFrequency: WorkboardTask.recurrence_frequency.NONE,
  recurrenceInterval: 1,
  recurrenceByWeekday: [],
  recurrenceBySetpos: null,
  recurrenceEndMode: 'none',
  recurrenceEndsOn: null,
  recurrenceCount: null,
});

export const validateCreateWorkboardTaskForm = (form: CreateWorkboardTaskFormState): string | null => {
  if (!form.name.trim()) {
    return 'Name is required.';
  }

  if (!form.occurrenceDate) {
    return 'Due date is required.';
  }

  if (form.recurrenceInterval < 1) {
    return 'Recurrence interval must be at least 1.';
  }

  const isRecurring = form.recurrenceFrequency !== WorkboardTask.recurrence_frequency.NONE;

  if (isRecurring) {
    if (form.recurrenceEndMode === 'date') {
      if (!form.recurrenceEndsOn) {
        return 'Recurrence end date is required.';
      }
      if (moment(form.recurrenceEndsOn).isBefore(moment(form.occurrenceDate), 'day')) {
        return 'Recurrence end date must be on or after the due date.';
      }
    }

    if (form.recurrenceEndMode === 'count' && (!form.recurrenceCount || form.recurrenceCount < 1)) {
      return 'Recurrence count must be at least 1.';
    }

    if (
      form.recurrenceFrequency === WorkboardTask.recurrence_frequency.WEEKLY &&
      form.recurrenceByWeekday.length === 0
    ) {
      return 'Select at least one weekday for weekly recurrence.';
    }

    if (
      form.recurrenceFrequency === WorkboardTask.recurrence_frequency.MONTHLY &&
      form.recurrenceBySetpos !== null &&
      form.recurrenceByWeekday.length === 0
    ) {
      return 'Select at least one weekday when using an ordinal monthly pattern.';
    }
  }

  return null;
};

const parseWeekdays = (rawWeekdays: WorkboardTask['recurrence_by_weekday']): string[] => {
  if (!Array.isArray(rawWeekdays)) {
    return [];
  }

  return rawWeekdays.filter((value): value is string => typeof value === 'string').map(value => value.toUpperCase());
};

const getRecurrenceEndMode = (task: WorkboardTask): RecurrenceEndMode => {
  if (task.recurrence_ends_on) {
    return 'date';
  }

  if (task.recurrence_count) {
    return 'count';
  }

  return 'none';
};

export const mapWorkboardTaskToFormState = (
  task: WorkboardTask,
  staffUsers: DashboardSmallUser[],
  occurrenceDateProp?: string | null,
  occurrenceStatus?: WorkboardTask.status | null
): CreateWorkboardTaskFormState => {
  const findUsers = (userIds?: number[] | null): SelectedUserWithGroup[] =>
    (userIds ?? [])
      .map(userId => staffUsers.find(user => user.id === userId) ?? null)
      .filter((user): user is DashboardSmallUser => user != null)
      .map(user => ({ ...user }));

  const startsOn = moment(task.starts_on, CALENDAR_DATE_FORMAT, true);
  const recurrenceEndMode = getRecurrenceEndMode(task);
  const parsedOccurrenceDate = occurrenceDateProp ? moment(occurrenceDateProp, CALENDAR_DATE_FORMAT, true) : null;
  const startsOnDate = startsOn.isValid() ? startsOn.toDate() : new Date();
  const occurrenceDate = parsedOccurrenceDate?.isValid() ? parsedOccurrenceDate.toDate() : startsOnDate;

  return {
    name: task.name,
    startsOn: startsOnDate,
    occurrenceDate,
    status: occurrenceStatus ?? task.status ?? WorkboardTask.status.PENDING,
    assignedTo: findUsers(task.assignees),
    assignedTaskManager: findUsers(task.task_managers),
    linkToRelatedDocuments: task.link_to_related_documents ?? '',
    fileReferenceFolder: task.file_reference_folder ?? '',
    isWaiting: task.is_waiting ?? false,
    recurrenceFrequency: task.recurrence_frequency ?? WorkboardTask.recurrence_frequency.NONE,
    recurrenceInterval: task.recurrence_interval ?? 1,
    recurrenceByWeekday: parseWeekdays(task.recurrence_by_weekday),
    recurrenceBySetpos: task.recurrence_by_setpos ?? null,
    recurrenceEndMode,
    recurrenceEndsOn:
      task.recurrence_ends_on && moment(task.recurrence_ends_on, CALENDAR_DATE_FORMAT, true).isValid()
        ? moment(task.recurrence_ends_on, CALENDAR_DATE_FORMAT).toDate()
        : null,
    recurrenceCount: task.recurrence_count ?? null,
  };
};

type BuildWorkboardTaskPayloadOptions = {
  forUpdate?: boolean;
};

export type WorkboardTaskPayload = WorkboardTask;

export const buildWorkboardTaskOccurrencePatchPayload = (
  form: CreateWorkboardTaskFormState
): { occurrence_date: string; status: WorkboardTask.status } => ({
  occurrence_date: moment(form.occurrenceDate!).format(CALENDAR_DATE_FORMAT),
  status: form.status,
});

export const buildWorkboardTaskPayload = (
  form: CreateWorkboardTaskFormState,
  options?: BuildWorkboardTaskPayloadOptions
): WorkboardTaskPayload => {
  const forUpdate = options?.forUpdate ?? false;
  const link = form.linkToRelatedDocuments.trim();
  const folder = form.fileReferenceFolder.trim();
  const isRecurring = form.recurrenceFrequency !== WorkboardTask.recurrence_frequency.NONE;
  const isOccurrenceEdit = forUpdate && isRecurring && form.occurrenceDate != null;
  const startsOnSource = isRecurring && forUpdate ? form.startsOn : (form.occurrenceDate ?? form.startsOn);

  const payload: WorkboardTaskPayload = {
    name: form.name.trim(),
    starts_on: moment(startsOnSource).format(CALENDAR_DATE_FORMAT),
    is_waiting: form.isWaiting,
    recurrence_frequency: form.recurrenceFrequency,
  };

  if (!isOccurrenceEdit) {
    payload.status = form.status;
  }

  if (forUpdate) {
    payload.assignees = form.assignedTo.map(user => user.id).filter((id): id is number => id != null);
    payload.task_managers = form.assignedTaskManager.map(user => user.id).filter((id): id is number => id != null);
    payload.link_to_related_documents = link || null;
    payload.file_reference_folder = folder || null;
  } else {
    if (form.assignedTo.length > 0) {
      payload.assignees = form.assignedTo.map(user => user.id).filter((id): id is number => id != null);
    }

    if (form.assignedTaskManager.length > 0) {
      payload.task_managers = form.assignedTaskManager.map(user => user.id).filter((id): id is number => id != null);
    }

    if (link) {
      payload.link_to_related_documents = link;
    }

    if (folder) {
      payload.file_reference_folder = folder;
    }
  }

  if (form.recurrenceFrequency === WorkboardTask.recurrence_frequency.NONE) {
    if (forUpdate) {
      payload.recurrence_interval = 1;
      payload.recurrence_by_weekday = null;
      payload.recurrence_by_setpos = null;
      payload.recurrence_ends_on = null;
      payload.recurrence_count = null;
    }
    return payload;
  }

  payload.recurrence_interval = form.recurrenceInterval;

  if (form.recurrenceByWeekday.length > 0) {
    payload.recurrence_by_weekday = form.recurrenceByWeekday;
  } else if (forUpdate) {
    payload.recurrence_by_weekday = null;
  }

  if (form.recurrenceFrequency === WorkboardTask.recurrence_frequency.MONTHLY && form.recurrenceBySetpos !== null) {
    payload.recurrence_by_setpos = form.recurrenceBySetpos;
  } else if (forUpdate) {
    payload.recurrence_by_setpos = null;
  }

  if (form.recurrenceEndMode === 'date' && form.recurrenceEndsOn) {
    payload.recurrence_ends_on = moment(form.recurrenceEndsOn).format(CALENDAR_DATE_FORMAT);
    if (forUpdate) {
      payload.recurrence_count = null;
    }
  } else if (form.recurrenceEndMode === 'count' && form.recurrenceCount) {
    payload.recurrence_count = form.recurrenceCount;
    if (forUpdate) {
      payload.recurrence_ends_on = null;
    }
  } else if (forUpdate) {
    payload.recurrence_ends_on = null;
    payload.recurrence_count = null;
  }

  return payload;
};
